
import { Component, EventEmitter, Input, Output, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { OnInit } from '@angular/core';

import { auditTime, Observable, Subscription } from 'rxjs';

import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MonitoringSourcePickerFieldModel } from '@Monitor/Shared/models/monitoring-source-picker-field.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { monitoringFiltersEnum } from '@Monitor/Shared/enums/monitoring-filters.enum';
import { MentionSearchService } from '@Monitor/Core/services/mention-search.service';
import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';
import { CoverageImageService } from '@Monitor/Briefings/services/coverage-image.service';
import { CoverageImage } from '@Monitor/Shared/models/coverage-image.model';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { RightSidebarComponent } from '@Common/Layout/components/RightSidebar/right-sidebar.component';

@Component({
    selector: 'agpr-coverage-image-dialog',
    templateUrl: './coverage-image-dialog.component.html',
    styleUrls: ['./coverage-image-dialog.component.scss'],
    providers: [
        CoverageImageService
    ],
    standalone: false
})

export class CoverageImageDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    public readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    // tuning knobs
    private readonly TRIGGER_PX = 100; // "near bottom"
    private readonly REARM_PX = 300;   // must scroll away from bottom to allow another load

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Output() clickOutside = new EventEmitter<void>();
    @Input() singleSelect: boolean = false;
    @Input() maxFiles: number = 1;
    @ViewChild('viewport') viewport!: CdkVirtualScrollViewport;
    @ViewChild('rightSidebar') public rightSidebar: RightSidebarComponent;

    private armed = true;
    hasMore = true; // set to false when backend says no more

    hideSlideOverlay: boolean = false;
    selectedItems: CoverageImage[] = [];
    focusedItem: CoverageImage;
    coverageImages: CoverageImage[] = [];
    rows: CoverageImage[][] = [];
    loading: boolean = false;
    selectedTabIndex: number = 0;
    totalMentionsCount: number = 0;
    dataSource: MonitoringBackEndDataSourceModel;
    maxFileReached: boolean = false;
    sourceField: MonitoringSourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        null,
        'backendDataSource',
        '',
        '',
        {
            dataSourceLabel: 'Monitoring.sourcePickerField.coverageSource',
            dateRangeLabel: 'Monitoring.sourcePickerField.dateRange',
            mentionCountLocalizationLabel: 'Monitoring.sourcePickerField.coverageSource.feedback',
            maxNumberOfMentions: 1000,
            dataSourceNullErrorMessage: 'Monitoring.sourcePickerField.dataSourceRequiredMessage',
            dataSourceOptions: [
                // eslint-disable-next-line max-len
                new DropdownOptionModel(`Briefings.coverageSource.${MonitoringSeriesTypeEnum.allCuratedCoverageInBrief}`, MonitoringSeriesTypeEnum.allCuratedCoverageInBrief),
                new DropdownOptionModel(`Briefings.coverageSource.${MonitoringSeriesTypeEnum.coverageInSection}`, MonitoringSeriesTypeEnum.coverageInSection),
                new DropdownOptionModel('Briefings.allSavedCoverage', MonitoringSeriesTypeEnum.allSavedCoverage),
                new DropdownOptionModel('Monitoring.source.type.folder', MonitoringSeriesTypeEnum.folder),
                new DropdownOptionModel('Monitoring.source.type.topic', MonitoringSeriesTypeEnum.topic),
                new DropdownOptionModel('Monitoring.source.type.subscription', MonitoringSeriesTypeEnum.subscription),
                new DropdownOptionModel('Monitoring.source.type.tag', MonitoringSeriesTypeEnum.tag),
                new DropdownOptionModel('Monitoring.source.type.tagGroup', MonitoringSeriesTypeEnum.tagGroup),
                new DropdownOptionModel('Monitoring.source.type.sourceGroup', MonitoringSeriesTypeEnum.sourceGroup),
                new DropdownOptionModel('Reports.series.type.socialTopic', MonitoringSeriesTypeEnum.socialTopic),
                new DropdownOptionModel('Reports.series.type.theme', MonitoringSeriesTypeEnum.theme),
                new DropdownOptionModel('Reports.series.type.aiAgent', MonitoringSeriesTypeEnum.aiAgent)
            ],
            showTooManyMentionsError: false,
            showFilters: true,
            orderedMercuryFilters: MentionSearchService.defaultOrderedMercuryFilters.filter((filter) => {
                // @TODO: This list should vary depending on the selected source, different options are available for saved vs unsaved mentions
                // likely needs a refactor
                return filter !== monitoringFiltersEnum.domainAuthority;
            }),
            separatedErrors: true
        }
    );

    private subscriptions: Subscription[] = [];


    constructor(
        public coreDialogService: CoreDialogService,
        private mentionSearchService: MentionSearchService,
        private coverageImageService: CoverageImageService,
    ) {
        super(coreDialogService, 'coverageImageDialog');
        this.dataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.allCuratedCoverageInBrief);
        this.mentionSearchService.clearFilters();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.mentionSearchService.getMentionCount().subscribe(x => this.totalMentionsCount = x),
            this.coverageImageService.loading$.subscribe(x => this.loading = x),
            this.coverageImageService.coverageImages$.subscribe(images => {
                this.coverageImages = this.coverageImages.concat(images);

                this.rows.length = 0;
                for (let i = 0; i < (this.coverageImages?.length ?? 0); i += 2) {
                    this.rows.push(this.coverageImages.slice(i, i + 2));
                }

                this.rows = [...this.rows];
            }),
            this.coverageImageService.mentionCount$.subscribe(count => {
                if (count !== null && count < 25) {
                    this.hasMore = false;
                }
            })
        );
    }

    ngAfterViewInit() {
        this.subscriptions.push(
            this.viewport.elementScrolled()
                .pipe(auditTime(80))
                .subscribe(() => this.onViewportScrolled())
        );

        this.updateSource();
    }

    private onViewportScrolled(): void {
        const bottomPx = this.viewport.measureScrollOffset('bottom');

        // re-arm only after user scrolls away from bottom enough
        if (bottomPx > this.REARM_PX) {
            this.armed = true;
        }

        if (!this.hasMore || this.loading || !this.armed) {
            return;
        };
        if (bottomPx > this.TRIGGER_PX) {
            return;
        };

        this.armed = false;
        if (this.rows.length > 10) {
            this.coverageImageService.searchCoverageImages();
        }
    }

    tabIndexChange(index: number) {
        this.selectedTabIndex = index;
    }

    onItemClicked(item: any): void {
        this.selectedTabIndex = 0;
    }

    cancel(): void {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    onFileSelect(): void {
        this.dialogClose.emit(this.selectedItems);
        this.closeDialog();
    }

    clearSelection(): void {
        this.selectedItems = [];
        this.maxFileReached = false;
        this.coverageImages.forEach(x => x.selected = false);
    }

    unSelect(item: CoverageImage): void {
        item.selected = false;
        this.maxFileReached = false;
        this.selectedItems = this.selectedItems.filter(x => x !== item);
    }

    updateSource(): void {
        this.hasMore = true;
        this.armed = true;
        this.coverageImages = [];
        this.rows = [];
        this.focusedItem = null;
        this.selectedItems = [];
        if (this.dataSource.type === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief
            || (this.dataSource.type === MonitoringSeriesTypeEnum.coverageInSection && !this.dataSource.sourceSearchCriteria)) {
            this.hasMore = false;
        }

        this.coverageImageService.updateSourceAndSearch(this.dataSource);
    }

    onCheckboxClick(item: CoverageImage): void {
        if (item.selected) {
            this.selectedItems.push(item);
            if (this.selectedItems.length >= this.maxFiles) {
                this.maxFileReached = true;
            }
        } else {
            this.selectedItems = this.selectedItems.filter(x => x !== item);
            this.maxFileReached = false;
        }

        this.selectedTabIndex = 1;
    }

    focusItem(item: CoverageImage): void {
        this.coverageImages.forEach(x => x.focused = false);
        item.focused = true;
        this.focusedItem = item;
        this.selectedTabIndex = 0;
        this.rightSidebar.scrollPanel.scrollTo();
    }

    getImageDimensions(event: Event, item: CoverageImage): void {
        const img = event.target as HTMLImageElement;
        item.width = img.naturalWidth;
        item.height = img.naturalHeight;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
